import React, { useEffect, useState } from "react";
import { X, Loader2, Sparkles, ArrowLeft, Tag, Check, AlertCircle } from "lucide-react";
import { api, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";

const PLAN_ORDER = ["free", "starter", "pro", "business"];
const planLabel = (k) => ({ free: "Free", starter: "Starter", pro: "Pro", business: "Business" }[k] || k);

const fmtChars = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return String(n || 0);
};

export default function UpgradeModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Screen: "plans" | "checkout"
  const [screen, setScreen] = useState("plans");
  const [selectedPlan, setSelectedPlan] = useState(null);
  // Checkout state
  const [couponCode, setCouponCode] = useState("");
  const [couponState, setCouponState] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [paying, setPaying] = useState(false);

  const loadSub = () => {
    setLoading(true);
    api.get("/billing/subscription")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    setScreen("plans");
    setSelectedPlan(null);
    setCouponCode(""); setCouponState(null); setCouponError("");
    loadSub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setCouponCode(""); setCouponState(null); setCouponError("");
    setScreen("checkout");
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true); setCouponError(""); setCouponState(null);
    try {
      const { data: cd } = await api.post("/billing/validate-coupon", {
        code: couponCode.trim(), context_key: selectedPlan?.key, amount: selectedPlan?.price_bdt || 0,
      });
      setCouponState(cd);
    } catch (err) {
      setCouponError(getErrorMessage(err) || "Invalid coupon");
    } finally { setCouponLoading(false); }
  };

  const handlePay = async () => {
    if (!selectedPlan) return;
    setPaying(true);
    try {
      const body = { type: "plan", plan: selectedPlan.key };
      if (couponState) body.coupon_code = couponState.code || couponCode.trim().toUpperCase();
      const { data: pd } = await api.post("/billing/initiate-payment", body);
      if (pd.direct) {
        if (pd.scheduled && pd.pending_plan === "free") {
          toast.success("Downgrade scheduled. Your current plan stays active until renewal.");
        } else {
          toast.success("Plan activated!");
        }
        setScreen("plans");
        loadSub();
        return;
      }
      if (pd.payment_url) window.location.href = pd.payment_url;
    } catch (err) {
      toast.error(getErrorMessage(err) || "Could not initiate payment");
    } finally { setPaying(false); }
  };

  if (!open) return null;

  const plans = (data?.plans || []).slice().sort((a, b) => PLAN_ORDER.indexOf(a.key) - PLAN_ORDER.indexOf(b.key));
  const finalAmount = couponState ? couponState.final_amount : selectedPlan?.price_bdt || 0;
  const discountAmount = couponState ? couponState.discount_amount : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full max-w-3xl rounded-3xl border border-cream-300 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-cream-300 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {screen === "checkout" && (
              <button onClick={() => setScreen("plans")} className="p-1.5 rounded-xl hover:bg-cream-100 text-ink-muted transition">
                <ArrowLeft size={15} />
              </button>
            )}
            <div>
              <p className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-coral">
                {screen === "checkout" ? "Order summary" : "Subscription"}
              </p>
              <h3 className="font-display text-[22px] font-semibold tracking-tight leading-tight">
                {screen === "checkout" ? `${planLabel(selectedPlan?.key)} plan` : "Upgrade your plan"}
              </h3>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-cream-100 text-ink-muted transition"><X size={15} /></button>
        </div>

        {loading || !data ? (
          <div className="p-10 text-ink-muted flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : screen === "plans" ? (
          /* ── Screen 1: Plan selection ── */
          <div className="p-6 space-y-5">
            {/* Current plan summary */}
            <div className="flex items-center justify-between bg-cream-100 rounded-2xl px-5 py-3.5 border border-cream-300">
              <div>
                <p className="text-[11px] text-ink-muted">Current plan</p>
                <p className="font-display text-[20px] font-semibold capitalize">{planLabel(data.plan)}</p>
              </div>
              <div className="text-right text-[12px] text-ink-muted">
                {data.plan_started_at && <p>Started {new Date(data.plan_started_at).toLocaleDateString("en-BD")}</p>}
                {data.plan_renews_at && data.plan !== "free" && <p>Renews {new Date(data.plan_renews_at).toLocaleDateString("en-BD")}</p>}
              </div>
            </div>

            {/* Plan cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {plans.map((p) => {
                const current = p.key === data.plan;
                return (
                  <div key={p.key} className={`rounded-2xl border p-4 flex flex-col ${current ? "border-coral bg-coral/5" : "border-cream-300 bg-white hover:border-ink/40 transition"}`}>
                    {(p.key === "pro" || p.key === "business") && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] font-bold bg-mint/25 text-ink px-2 py-0.5 rounded-full mb-2 w-fit">
                        <Sparkles size={9} /> HD Audio
                      </span>
                    )}
                    <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-muted">{planLabel(p.key)}</p>
                    <p className="font-display text-[24px] font-bold leading-none mt-1">
                      ৳{Number(p.price_bdt || 0).toLocaleString()}
                      <span className="text-[11px] font-normal text-ink-muted">/mo</span>
                    </p>
                    <p className="text-[11px] text-ink-muted mt-1 mb-3">{fmtChars(Number(p.monthly_chars || 0))} chars</p>
                    <button
                      disabled={current}
                      onClick={() => handleSelectPlan(p)}
                      className={`mt-auto w-full rounded-full py-2 text-[12px] font-semibold transition ${
                        current
                          ? "bg-ink text-cream-100 cursor-default opacity-80"
                          : "bg-cream-100 text-ink border border-cream-300 hover:bg-ink hover:text-cream-100 hover:border-ink"
                      }`}
                    >
                      {current ? "Current" : p.key === data.plan ? "Renew" : "Select"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* ── Screen 2: Checkout ── */
          <div className="p-6 space-y-5 max-w-md mx-auto w-full">
            {/* Item summary */}
            <div className="bg-cream-100 rounded-2xl px-5 py-4 border border-cream-300 space-y-1">
              <p className="font-semibold text-[16px]">{planLabel(selectedPlan?.key)} Plan (monthly)</p>
              <p className="text-[13px] text-ink-muted">{fmtChars(Number(selectedPlan?.monthly_chars || 0))} characters/month</p>
              <p className="text-[12px] text-ink-muted">{selectedPlan?.quality}</p>
            </div>

            {/* Coupon */}
            <div>
              <p className="text-[11px] font-semibold text-ink-muted uppercase tracking-[0.18em] mb-2">Coupon code</p>
              <div className="flex gap-2">
                <input
                  placeholder="e.g. WELCOME20"
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponState(null); setCouponError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleValidateCoupon()}
                  disabled={!!couponState}
                  className="flex-1 px-3.5 py-2.5 border border-cream-300 rounded-xl text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-coral/40 disabled:opacity-60"
                />
                {couponState ? (
                  <button onClick={() => { setCouponState(null); setCouponCode(""); }}
                    className="px-4 py-2.5 border border-cream-300 rounded-xl text-[12px] font-semibold text-ink-muted hover:bg-cream-100 transition">
                    Remove
                  </button>
                ) : (
                  <button onClick={handleValidateCoupon} disabled={couponLoading || !couponCode.trim()}
                    className="px-4 py-2.5 bg-ink text-cream-100 rounded-xl text-[12px] font-semibold hover:bg-coral transition disabled:opacity-40 flex items-center gap-1.5 min-w-[80px] justify-center">
                    {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Tag size={11}/> Apply</>}
                  </button>
                )}
              </div>
              {couponError && (
                <div className="mt-2 flex items-center gap-1.5 text-[12px] text-red-600">
                  <AlertCircle size={12}/> {couponError}
                </div>
              )}
              {couponState && (
                <div className="mt-2 flex items-center gap-1.5 text-[12px] text-mint-dark font-semibold">
                  <Check size={12}/> {couponState.description || `${couponState.discount_pct || ""}% off applied`}
                </div>
              )}
            </div>

            {/* Price breakdown */}
            <div className="border-t border-cream-300 pt-4 space-y-2">
              <div className="flex justify-between text-[13px] text-ink-muted">
                <span>Subtotal</span>
                <span>৳{Number(selectedPlan?.price_bdt || 0).toLocaleString()}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-[13px] text-mint-dark font-semibold">
                  <span>Discount</span>
                  <span>-৳{Number(discountAmount).toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-[15px] font-bold text-ink pt-1 border-t border-cream-300">
                <span>Total</span>
                <span>৳{Number(finalAmount).toLocaleString()}</span>
              </div>
            </div>

            <button
              onClick={handlePay}
              disabled={paying}
              className="w-full bg-ink hover:bg-coral text-cream-100 rounded-full py-3.5 text-[14px] font-semibold transition flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {paying ? <><Loader2 size={14} className="animate-spin" /> Processing…</> : (selectedPlan?.key === "free" ? "Schedule downgrade to Free" : `Pay ৳${Number(finalAmount).toLocaleString()} via bKash / Nagad`)}
            </button>
            <p className="text-[11px] text-ink-muted text-center">Secure payment via Paymently · Your plan activates instantly</p>
          </div>
        )}
      </div>
    </div>
  );
}
